<template>
	<div
		v-if="visibleSections.length"
		class="bot-menu-sections"
	>
		<div
			v-for="section in visibleSections"
			:key="section.id"
			class="bot-menu-sections__group"
		>
			<v-list-item
				class="bot-menu-sections__section"
				@click.stop="toggleSection(section.id)"
			>
				<v-list-item-title class="menu-item bot-menu-sections__section-title">
					<div>
						<v-icon
							v-if="section.icon"
							color="greyBlueLight"
						>{{ section.icon }}</v-icon>
					</div>
					<span>{{ section.title }}</span>
					<v-spacer />
					<v-icon
						small
						color="greyBlueLight"
						class="bot-menu-sections__chevron"
						:class="{ 'bot-menu-sections__chevron--open': openedSection === section.id }"
					>mdi-chevron-down</v-icon>
				</v-list-item-title>
			</v-list-item>

			<v-expand-transition>
				<div
					v-show="openedSection === section.id"
					class="bot-menu-sections__items"
				>
					<v-list-item
						v-for="item in section.userItems"
						:key="`user-${item.name}`"
						class="bot-menu-sections__item"
						:class="{ 'bot-menu-sections__item--active': isActiveItem(item) }"
						@click.stop="navigateToModel(item)"
					>
						<v-list-item-title class="bot-menu-sections__item-title">
							{{ item.title }}
						</v-list-item-title>
					</v-list-item>

					<v-divider
						v-if="section.userItems.length && section.adminItems.length"
						class="bot-menu-sections__audience-divider"
					/>

					<v-list-item
						v-for="item in section.adminItems"
						:key="`admin-${item.name}`"
						class="bot-menu-sections__item"
						:class="{ 'bot-menu-sections__item--active': isActiveItem(item) }"
						@click.stop="navigateToModel(item)"
					>
						<v-list-item-title class="bot-menu-sections__item-title">
							{{ item.title }}
						</v-list-item-title>
					</v-list-item>
				</div>
			</v-expand-transition>
		</div>

		<v-divider class="bot-menu-sections__divider" />
	</div>
</template>

<script>
const BOT_MENU_SECTIONS = [
	{ id: 'address_program', icon: 'mdi-map-marker-path' },
	{ id: 'audits', icon: 'mdi-clipboard-check-outline' },
	{ id: 'trade_points', icon: 'mdi-store-outline' },
	{ id: 'users', icon: 'mdi-account-group-outline' },
	{ id: 'instructions_and_templates', icon: 'mdi-book-open-page-variant-outline' },
	{ id: 'settings', icon: 'mdi-cog-outline' },
	{ id: 'ba', icon: 'mdi-chart-bar' },
	{ id: 'contacts', icon: 'mdi-card-account-phone-outline' },
	{ id: 'products_and_categories', icon: 'mdi-package-variant-closed' },
	{ id: 'presentations', icon: 'mdi-presentation' },
];

export default {
	name: 'BotMenuSections',

	props: {
		tables: {
			type: Array,
			default: () => [],
		},
		botId: {
			type: [Number, String],
			default: null,
		},
		currentUser: {
			type: Object,
			default: null,
		},
	},

	data: () => ({
		openedSection: null,
	}),

	watch: {
		visibleSections: {
			handler() {
				this.openActiveSection('visibleSections');
			},
			immediate: true,
		},
		'$route.params.model_name'() {
			this.openActiveSection('routeModel');
		},
	},

	computed: {
		visibleSections() {
			return BOT_MENU_SECTIONS
				.map((section) => {
					const items = this.tables
						.filter((table) => this.isTableInSection(table, section.id))
						.filter((table) => this.canShowTable(table))
						.map((table) => this.buildMenuItem(table));
					const userItems = items
						.filter((item) => item.audience === 'user')
						.sort(this.sortMenuItems);
					const adminItems = items
						.filter((item) => item.audience === 'admin')
						.sort(this.sortMenuItems);

					if (!items.length) {
						return null;
					}

					return {
						...section,
						title: this.$t(`modules.dialog.texts.settings.bot_menu_sections.${section.id}`),
						items: [...userItems, ...adminItems],
						userItems,
						adminItems,
					};
				})
				.filter(Boolean);
		},
	},

	methods: {
		getBotMenuConfig(table) {
			return table?.front_json?.bot_menu || {};
		},

		isTableInSection(table, sectionId) {
			return this.getBotMenuConfig(table)?.section === sectionId;
		},

		canShowTable(table) {
			if (!table?.name) {
				return false;
			}

			if (this.currentUser?.root) {
				return true;
			}

			const botId = this.botId || this.$route?.params?.id;
			const modelsPermissions = this.currentUser?.role?.permissions?.custom_data?.models?.[botId];

			if (!modelsPermissions) {
				return true;
			}

			return !!modelsPermissions?.[table.name]?.canShow;
		},

		buildMenuItem(table) {
			const config = this.getBotMenuConfig(table);
			const parsedOrder = parseInt(config.order, 10);

			return {
				name: table.name,
				title: config.title || table.description || table.name,
				order: Number.isNaN(parsedOrder) ? Infinity : parsedOrder,
				audience: this.normalizeAudience(config.audience),
			};
		},

		normalizeAudience(audience) {
			return audience === 'admin' ? 'admin' : 'user';
		},

		sortMenuItems(a, b) {
			if (a.order !== b.order) {
				return a.order - b.order;
			}

			return String(a.title).localeCompare(String(b.title));
		},

		toggleSection(sectionId) {
			const nextOpenedSection = this.openedSection === sectionId ? null : sectionId;
			this.openedSection = nextOpenedSection;
		},

		openActiveSection(reason) {
			const activeModelName = this.$route?.params?.model_name;

			if (!activeModelName) {
				return;
			}

			const activeSection = this.visibleSections.find((section) => {
				return section.items.some((item) => item.name === activeModelName);
			});

			if (activeSection) {
				this.openedSection = activeSection.id;
			}
		},

		isActiveItem(item) {
			return this.$route?.params?.model_name === item?.name;
		},

		navigateToModel(item) {
			const botId = this.botId || this.$route?.params?.id;

			if (!botId || !item?.name) {
				return;
			}

			const path = `/customdata/${botId}/models/${encodeURIComponent(item.name)}`;

			if (this.$route?.path !== path) {
				this.$router.push({ path }).catch(() => {});
			}

			this.$emit('navigate');
		},
	},
};
</script>

<style lang="scss" scoped>
.bot-menu-sections {
	&__section-title {
		min-width: 0;
	}

	&__chevron {
		transition: transform 0.25s ease;
	}

	&__chevron--open {
		transform: rotate(180deg);
	}

	&__items {
		overflow: hidden;
	}

	&__item {
		min-height: 38px;
		padding-left: 56px !important;

		&--active {
			color: var(--v-primary-base);
			font-weight: 600;
		}
	}

	&__item-title {
		font-size: 14px;
		line-height: 18px;
		white-space: normal;
		word-break: break-word;
	}

	&__divider {
		margin-top: 4px;
	}

	&__audience-divider {
		margin: 4px 16px 4px 56px;
	}
}
</style>
